/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectSipProfile.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting a SIP Profile with search functionality.
    Used in extensions for selecting the SIP profile for devices.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by SIP profile name and description
    - Loads profiles from props (no API calls)

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, Settings } from 'lucide-react';

interface SipProfile {
  id: number;
  name: string;
  description?: string;
}

interface SelectSipProfileProps {
  id?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  sipProfiles: SipProfile[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  allowNull?: boolean;
  nullLabel?: string;
}

export default function SelectSipProfile({
  id,
  value,
  onChange,
  sipProfiles,
  placeholder = 'Select SIP Profile',
  disabled = false,
  className = '',
  error = false,
  allowNull = true,
  nullLabel = 'None (inherit from profile)'
}: SelectSipProfileProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return sipProfiles;
    }
    
    const query = searchQuery.toLowerCase();
    return sipProfiles.filter(profile => 
      profile.name.toLowerCase().includes(query) ||
      (profile.description && profile.description.toLowerCase().includes(query))
    );
  }, [searchQuery, sipProfiles]);

  // Get label for selected value
  const selectedLabel = useMemo(() => {
    if (value === null) {
      return nullLabel;
    }
    const selected = sipProfiles.find(profile => profile.id === value);
    return selected ? selected.name : placeholder;
  }, [value, sipProfiles, placeholder, nullLabel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle selection
  const handleSelect = (profileId: number | null) => {
    onChange(profileId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleSelect(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${error ? 'border-red-500' : ''}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Settings className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {selectedLabel}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {value !== null && (
            <span
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer text-lg leading-none"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e as any);
                }
              }}
            >
              ×
            </span>
          )}
          <ChevronDown 
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search SIP profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No SIP profiles found
              </div>
            ) : (
              <>
                {allowNull && (
                  <button
                    type="button"
                    onClick={() => handleSelect(null)}
                    className={`
                      w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                      flex items-center gap-2
                      ${value === null ? 'bg-primary/10 text-primary' : 'text-foreground'}
                    `}
                  >
                    <Settings className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{nullLabel}</span>
                  </button>
                )}
                {filteredOptions.map((profile) => (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => handleSelect(profile.id)}
                    className={`
                      w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                      ${value === profile.id ? 'bg-primary/10 text-primary' : 'text-foreground'}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">{profile.name}</div>
                        {profile.description && (
                          <div className="truncate text-xs text-muted-foreground">
                            {profile.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} profile{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
}
